import { useEffect, useState } from 'react';
import api from '../services/ApiService';
import axios from 'axios';
import { ZoomTable, ZoomBadge, ZoomBtn, ZoomModal, ZoomField } from './zoom/ZoomTable';

/* Sites: per-tenant site with an extension range and its outbound gateways
   (first = default). Drives Users -> Add User: suggested extension, the DID
   pool offered, and the gateway a user without a DID dials out through.
   Click a site to see its extensions and DIDs; the shortcuts open Users /
   Extensions / DIDs filtered to that site (via onOpen). */
type SiteDraft = { id?: number; name: string; ext_start: string; ext_end: string; gateways: string[] };
const EMPTY: SiteDraft = { name: '', ext_start: '', ext_end: '', gateways: [] };

const statCard: React.CSSProperties = { flex: '1 1 160px', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 16px', background: '#fff' };
const statNum: React.CSSProperties = { fontSize: 24, fontWeight: 700 };
const statLbl: React.CSSProperties = { fontSize: 12, color: '#6b7280' };

/* Locations under sites (2026-10-20) — managed by advanced users only. A location
   (e.g. "Arsala Tower – Level 24") groups users, extensions and numbers for billing. */
function LocationsSection({ sites }: { sites: any[] }) {
  const [adv, setAdv] = useState(false);
  const [locs, setLocs] = useState<any[]>([]);
  const [texts, setTexts] = useState<any[]>([]);
  const [ed, setEd] = useState<any>(null);
  const [msg, setMsg] = useState('');
  const [showTidy, setShowTidy] = useState(false);
  const [lq, setLq] = useState<any>({ enabled: false, queues: [], links: [] });   // location queues (2026-10-27)
  const qIdsOf = (lid: any) => (lq.links || []).filter((x: any) => String(x.location_id) === String(lid)).map((x: any) => Number(x.queue_id));
  const load = () => {
    axios.get('/api/locations/queue-links').then((r) => setLq(r.data || { enabled: false, queues: [], links: [] })).catch(() => {});
    axios.get('/api/locations').then((r) => setLocs(Array.isArray(r.data) ? r.data : [])).catch(() => setLocs([]));
    axios.get('/api/locations/text-values').then((r) => setTexts(Array.isArray(r.data) ? r.data : [])).catch(() => setTexts([]));
  };
  useEffect(() => { axios.get('/api/locations/can-manage').then((r) => setAdv(!!r.data?.manage)).catch(() => {}); load(); }, []);
  const e2 = (e: any) => e?.response?.data?.error || e?.message || 'Something went wrong';
  const save = async () => {
    setMsg('');
    try {
      const r: any = ed.id ? await axios.put(`/api/locations/${ed.id}`, ed) : await axios.post('/api/locations', ed);
      const lid = ed.id || r?.data?.id || r?.data?.location?.id;
      if (lq.enabled && lid) await axios.put(`/api/locations/${lid}/queues`, { queue_ids: ed.queue_ids || [] });
      setEd(null); load();
    } catch (e) { setMsg(e2(e)); }
  };
  const del = async (l: any) => {
    if (!window.confirm(`Delete location "${l.name}"? ${l.users} user(s), ${l.extensions} extension(s) and ${l.dids} number(s) become "not set". Past invoices keep their breakdown.`)) return;
    try { await axios.delete(`/api/locations/${l.id}`); load(); } catch (e) { alert(e2(e)); }
  };
  const mapText = async (t: any, lid: any) => {
    try { await axios.post('/api/locations/map-text', { text: t.text, location_id: lid || null }); load(); } catch (e) { alert(e2(e)); }
  };
  return (
    <div style={{ marginTop: 18 }}>
      <ZoomTable title="Locations" subtitle={adv ? 'Floors / buildings inside each site (e.g. Arsala Tower – Level 24). Used to group users, extensions and numbers and to break invoices down by location. Names must be unique.' : 'Managed by location managers. Choose a location on users and numbers.'}
        actions={adv ? <ZoomBtn variant="primary" onClick={() => { setMsg(''); setEd({ name: '', code: '', address: '', site_id: sites[0]?.id || '', is_active: 1, queue_ids: [] }); }}>+ New location</ZoomBtn> : undefined}
        columns={[
          { key: 'n', label: 'Location', render: (l: any) => <div><div className="z-tbl-primary-cell">{l.name}</div><div className="z-tbl-sub">{[l.code, l.address].filter(Boolean).join(' · ')}</div></div> },
          { key: 's', label: 'Site', render: (l: any) => l.site_name || '—' },
          { key: 'u', label: 'Users', render: (l: any) => String(l.users) },
          { key: 'e', label: 'Extensions', render: (l: any) => String(l.extensions) },
          { key: 'd', label: 'Numbers', render: (l: any) => String(l.dids) },
          ...(lq.enabled ? [{ key: 'q', label: 'Queues', render: (l: any) => (lq.queues || []).filter((q: any) => qIdsOf(l.id).includes(Number(q.id))).map((q: any) => q.display_name || q.queue_name).join(', ') || '—' }] : []),
          { key: 'a', label: 'Status', render: (l: any) => <ZoomBadge text={Number(l.is_active) ? 'Active' : 'Off'} tone={Number(l.is_active) ? 'green' : 'gray'} /> },
        ]}
        rows={locs} rowKey={(l: any) => l.id} selectable={false}
        rowActions={adv ? (l: any) => (<><ZoomBtn onClick={() => { setMsg(''); setEd({ ...l, queue_ids: qIdsOf(l.id) }); }}>Edit</ZoomBtn><ZoomBtn variant="danger" onClick={() => del(l)}>Delete</ZoomBtn></>) : undefined}
        emptyText="No locations yet" />
      {adv && texts.length > 0 && (
        <div style={{ border: '1px solid #fcd34d', background: '#fffbeb', borderRadius: 10, padding: '8px 12px', marginTop: 12, fontSize: 13 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span><b>{texts.reduce((n: number, t: any) => n + Number(t.users || 0), 0)} user(s)</b> still have an old free-text location from before locations existed.</span>
            <button type="button" onClick={() => setShowTidy(!showTidy)} style={{ width: 'auto', border: '1px solid #d1d5db', background: '#fff', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}>{showTidy ? 'Hide' : 'Review'}</button>
          </div>
          {showTidy && (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
              <thead><tr style={{ textAlign: 'left', color: '#6b7280', fontSize: 12 }}><th style={{ padding: '4px 6px' }}>Old text</th><th>Users</th><th>Link to a location</th><th></th></tr></thead>
              <tbody>{texts.map((t: any) => (
                <tr key={t.text} style={{ borderTop: '1px solid #fde68a' }}>
                  <td style={{ padding: '6px', fontWeight: 600 }}>"{t.text}"</td>
                  <td style={{ width: 70 }}>{t.users}</td>
                  <td style={{ width: 280 }}>
                    <select defaultValue="" onChange={(e) => e.target.value && mapText(t, Number(e.target.value))} style={{ width: 260, maxWidth: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }}>
                      <option value="">{locs.length ? 'Choose location…' : 'Create a location first'}</option>{locs.map((l: any) => <option key={l.id} value={l.id}>{l.site_name ? l.site_name + ' · ' : ''}{l.name}</option>)}
                    </select></td>
                  <td style={{ width: 70, textAlign: 'right' }}>
                    <button type="button" onClick={() => window.confirm(`Clear "${t.text}" on ${t.users} user(s)?`) && mapText(t, null)} style={{ width: 'auto', border: '1px solid #d1d5db', background: '#fff', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}>Clear</button></td>
                </tr>))}</tbody>
            </table>)}
        </div>)}
      {ed && (
        <ZoomModal width={620} title={ed.id ? `Edit location — ${ed.name}` : 'New location'} onClose={() => setEd(null)}
          footer={<><ZoomBtn onClick={() => setEd(null)}>Cancel</ZoomBtn><ZoomBtn variant="primary" onClick={save}>Save</ZoomBtn></>}>
          {msg && <div style={{ color: '#b91c1c', marginBottom: 8 }}>{msg}</div>}
          <div className="z-field-grid">
            <ZoomField label="Site"><select value={ed.site_id || ''} onChange={(e) => setEd({ ...ed, site_id: Number(e.target.value) })}>{sites.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></ZoomField>
            <ZoomField label="Location name" hint="Unique, e.g. Arsala Tower – Level 24"><input value={ed.name} onChange={(e) => setEd({ ...ed, name: e.target.value })} /></ZoomField>
            <ZoomField label="Short code" hint="Optional, e.g. AUH-ARS-24"><input value={ed.code || ''} onChange={(e) => setEd({ ...ed, code: e.target.value })} /></ZoomField>
            <ZoomField label="Address" hint="Optional"><input value={ed.address || ''} onChange={(e) => setEd({ ...ed, address: e.target.value })} /></ZoomField>
            <ZoomField label="Status"><select value={Number(ed.is_active) ? '1' : '0'} onChange={(e) => setEd({ ...ed, is_active: Number(e.target.value) })}><option value="1">Active</option><option value="0">Off (hidden from pickers)</option></select></ZoomField>
            {lq.enabled && (
              <ZoomField label="Queues for this location" hint="New customers in this location ring one of these (chosen in the customer wizard). Usually one or two.">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
                  {(lq.queues || []).length ? (lq.queues || []).map((q: any) => (
                    <label key={q.id} style={{ fontSize: 13, whiteSpace: 'nowrap' }}><input type="checkbox" style={{ width: 'auto', marginRight: 4 }} checked={(ed.queue_ids || []).includes(Number(q.id))}
                      onChange={(e) => setEd({ ...ed, queue_ids: e.target.checked ? [...(ed.queue_ids || []), Number(q.id)] : (ed.queue_ids || []).filter((x: number) => x !== Number(q.id)) })} />{q.display_name || q.queue_name}</label>))
                    : <span className="z-tbl-sub">No queues yet — create them under Queue Management.</span>}
                </div></ZoomField>)}
          </div>
        </ZoomModal>)}
    </div>);
}

export default function PbxSitesPage({ onOpen }: { onOpen?: (siteId: any, page: string) => void }) {
  const [sites, setSites] = useState<any[]>([]);
  const [gateways, setGateways] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');
  const [edit, setEdit] = useState<SiteDraft | null>(null);
  const [err, setErr] = useState('');
  const [detail, setDetail] = useState<any>(null);

  const load = () => {
    setLoading(true); setLoadErr('');
    (api as any).getPbxSites()
      .then((d: any) => { setSites(d.sites || []); setGateways(d.gateways || []); })
      .catch((e: any) => setLoadErr(e?.response?.status === 403
        ? 'You do not have access to Sites in this session. Sign out and sign in again.'
        : (e?.response?.data?.error || 'Could not load sites')))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openDetail = (s: any) => {
    setDetail({ site: s, loading: true });
    (api as any).getPbxSiteSummary(s.id)
      .then((d: any) => setDetail({ ...d, loading: false }))
      .catch((e: any) => setDetail({ site: s, loading: false, error: e?.response?.data?.error || 'Could not load site' }));
  };

  const gwName = (n: string) => {
    const g = gateways.find((x: any) => x.trunk_name === n);
    return (g && (g.display_name || g.trunk_name)) || n;
  };
  const toggleGw = (n: string) => {
    if (!edit) return;
    const has = edit.gateways.includes(n);
    setEdit({ ...edit, gateways: has ? edit.gateways.filter((x) => x !== n) : [...edit.gateways, n] });
  };
  const makeDefault = (n: string) => {
    if (!edit) return;
    setEdit({ ...edit, gateways: [n, ...edit.gateways.filter((x) => x !== n)] });
  };
  const startEdit = (s: any) => { setErr(''); setEdit({ id: s.id, name: s.name, ext_start: String(s.ext_start), ext_end: String(s.ext_end), gateways: [...(s.gateways || [])] }); };
  const save = async () => {
    if (!edit) return;
    setErr('');
    const body = { name: edit.name.trim(), ext_start: Number(edit.ext_start), ext_end: Number(edit.ext_end), gateways: edit.gateways };
    try {
      if (edit.id) await (api as any).updatePbxSite(edit.id, body);
      else await (api as any).createPbxSite(body);
      const wasId = edit.id;
      setEdit(null);
      load();
      if (detail && wasId && detail.site && detail.site.id === wasId) openDetail({ ...detail.site, id: wasId });
    } catch (e: any) {
      setErr(e?.response?.data?.error || 'Save failed');
    }
  };
  const remove = async (s: any) => {
    if (!window.confirm('Remove site ' + s.name + '? Existing users keep their extensions and gateways.')) return;
    try { await (api as any).deletePbxSite(s.id); setDetail(null); load(); } catch (e: any) { alert(e?.response?.data?.error || 'Remove failed'); }
  };

  const gwBadges = (list: string[]) => (
    <span>{(list || []).map((g: string, i: number) => (
      <span key={g} style={{ marginRight: 6 }}>{i === 0 ? <ZoomBadge text={gwName(g) + ' (default)'} tone="blue" /> : <ZoomBadge text={gwName(g)} />}</span>
    ))}</span>
  );

  const modal = edit && (
    <ZoomModal
      title={edit.id ? 'Edit Site' : 'Add Site'}
      onClose={() => setEdit(null)}
      footer={<><ZoomBtn onClick={() => setEdit(null)}>Cancel</ZoomBtn><ZoomBtn variant="primary" onClick={save}>Save</ZoomBtn></>}
    >
      {err && <div style={{ color: '#b91c1c', marginBottom: 10 }}>{err}</div>}
      <div className="z-field-grid">
        <ZoomField label="Site name"><input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="e.g. Abu Dhabi" /></ZoomField>
        <ZoomField label="Extensions from"><input value={edit.ext_start} onChange={(e) => setEdit({ ...edit, ext_start: e.target.value.replace(/[^0-9]/g, '') })} placeholder="4000" /></ZoomField>
        <ZoomField label="Extensions to"><input value={edit.ext_end} onChange={(e) => setEdit({ ...edit, ext_end: e.target.value.replace(/[^0-9]/g, '') })} placeholder="4999" /></ZoomField>
      </div>
      <ZoomField label="Gateways" hint="Users without a DID dial out via the default gateway. Users with a DID dial out via the DID's gateway.">
        <div>
          {gateways.length === 0 && <div>No gateways available for this tenant.</div>}
          {gateways.map((g: any) => {
            const on = edit.gateways.includes(g.trunk_name);
            const isDefault = edit.gateways[0] === g.trunk_name;
            return (
              <div key={g.trunk_name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 220 }}>
                  <input type="checkbox" checked={on} onChange={() => toggleGw(g.trunk_name)} />
                  {g.display_name || g.trunk_name}
                </label>
                {on && (isDefault
                  ? <ZoomBadge text="Default" tone="blue" />
                  : <ZoomBtn onClick={() => makeDefault(g.trunk_name)}>Make default</ZoomBtn>)}
              </div>
            );
          })}
        </div>
      </ZoomField>
    </ZoomModal>
  );

  // ---------------- site detail view
  if (detail) {
    const s = detail.site || {};
    const exts: any[] = detail.extensions || [];
    const dids: any[] = detail.dids || [];
    const size = Number(s.ext_end) - Number(s.ext_start) + 1;
    const assigned = dids.filter((d) => !d.available).length;
    const available = dids.filter((d) => d.available && Number(d.is_active) === 1).length;
    const tdg = detail.tenant_default_gateway;
    const extCols = [
      { key: 'ext', label: 'Extension', render: (e: any) => <div className="z-tbl-primary-cell">{e.extension_number}</div> },
      { key: 'name', label: 'Name / user', render: (e: any) => (
        <div>{e.display_name || '—'}{e.username && <div className="z-tbl-sub">{e.username}</div>}</div>
      ) },
      { key: 'cli', label: 'Caller ID (DID)', render: (e: any) => e.outbound_cid || '—' },
      { key: 'gw', label: 'Dials out via', render: (e: any) => e.gateway
        ? (e.gateway_display || e.gateway)
        : ('Tenant default' + (tdg ? ' (' + (tdg.display_name || tdg.trunk_name) + ')' : '')) },
      { key: 'st', label: 'Status', render: (e: any) => <ZoomBadge text={Number(e.is_active) === 1 ? 'Active' : 'Disabled'} tone={Number(e.is_active) === 1 ? 'green' : 'red'} /> },
    ];
    const didCols = [
      { key: 'num', label: 'DID', render: (d: any) => <div className="z-tbl-primary-cell">{d.did_number}</div> },
      { key: 'gw', label: 'Gateway', render: (d: any) => gwName(d.trunk_name) },
      { key: 'st', label: 'Status', render: (d: any) => Number(d.is_active) !== 1
        ? <ZoomBadge text="Inactive" tone="gray" />
        : d.available
          ? <ZoomBadge text="Available" tone="green" />
          : <ZoomBadge text={'Assigned: ' + (d.routing_type || '') + ' ' + (d.destination_value || '')} tone="blue" /> },
    ];
    return (
      <>
        {modal}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
          <ZoomBtn onClick={() => setDetail(null)}>&lsaquo; All sites</ZoomBtn>
          <h2 style={{ margin: 0 }}>{s.name}</h2>
          <ZoomBadge text={'Extensions ' + s.ext_start + ' - ' + s.ext_end} tone="blue" />
          {gwBadges(s.gateways)}
          <span style={{ flex: 1 }} />
          <ZoomBtn onClick={() => startEdit(s)}>Edit site</ZoomBtn>
        </div>
        {detail.error && <div style={{ color: '#b91c1c', marginBottom: 10 }}>{detail.error}</div>}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
          <div style={statCard}><div style={statNum}>{exts.length}</div><div style={statLbl}>extensions used of {size}</div></div>
          <div style={statCard}><div style={statNum}>{assigned}</div><div style={statLbl}>DIDs assigned</div></div>
          <div style={statCard}><div style={statNum}>{available}</div><div style={statLbl}>DIDs available</div></div>
        </div>
        {onOpen && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
            <ZoomBtn variant="primary" onClick={() => onOpen(s.id, 'Users')}>Users at {s.name}</ZoomBtn>
            <ZoomBtn onClick={() => onOpen(s.id, 'Extensions')}>Extensions at {s.name}</ZoomBtn>
            <ZoomBtn onClick={() => onOpen(s.id, 'DIDs')}>DIDs at {s.name}</ZoomBtn>
          </div>
        )}
        <ZoomTable title="Extensions" subtitle={'Extensions in the ' + s.name + ' range'} columns={extCols} rows={exts}
          rowKey={(e: any) => e.id} selectable={false} emptyText={detail.loading ? 'Loading...' : 'No extensions in this range yet'} />
        <div style={{ height: 18 }} />
        <ZoomTable title="DIDs" subtitle={'DIDs arriving on ' + s.name + ' gateways'} columns={didCols} rows={dids}
          rowKey={(d: any) => d.id} selectable={false} emptyText={detail.loading ? 'Loading...' : 'No DIDs on these gateways'} />
      </>
    );
  }

  // ---------------- site list
  const columns = [
    { key: 'name', label: 'Site', render: (s: any) => (
      <a href="#" className="z-tbl-primary-cell" style={{ textDecoration: 'none' }} onClick={(e) => { e.preventDefault(); openDetail(s); }}>{s.name} &rsaquo;</a>
    ) },
    { key: 'range', label: 'Extension range', render: (s: any) => s.ext_start + ' - ' + s.ext_end },
    { key: 'gw', label: 'Gateways', render: (s: any) => gwBadges(s.gateways) },
    { key: 'status', label: 'Status', render: (s: any) => <ZoomBadge text={Number(s.is_active) === 1 ? 'Active' : 'Inactive'} tone={Number(s.is_active) === 1 ? 'green' : 'gray'} /> },
  ];

  return (
    <>
      {modal}
      {loadErr && <div style={{ color: '#b91c1c', marginBottom: 12 }}>{loadErr}</div>}
      <ZoomTable
        title="Sites"
        subtitle="Each site has its own extension range and outbound gateways. Click a site to manage it."
        actions={<ZoomBtn variant="primary" onClick={() => { setErr(''); setEdit({ ...EMPTY }); }}>+ Add Site</ZoomBtn>}
        columns={columns}
        rows={sites}
        rowKey={(s: any) => s.id}
        selectable={false}
        rowActions={(s: any) => (
          <>
            <ZoomBtn onClick={() => openDetail(s)}>Manage</ZoomBtn>
            <ZoomBtn onClick={() => startEdit(s)}>Edit</ZoomBtn>
            <ZoomBtn variant="danger" onClick={() => remove(s)}>Remove</ZoomBtn>
          </>
        )}
        emptyText={loading ? 'Loading...' : 'No sites yet'}
      />
      <LocationsSection sites={sites} />
    </>
  );
}
